/**
 * 랜딩(`/`)에 보이는 글자 전부. 마크업을 건드리지 않고 문구만 고칠 수 있게 한 곳에 모았다.
 *
 * 사실 확인 근거 — 바꿀 때 같이 확인할 것:
 *  - 4개 언어: `lib/language.ts` SUPPORTED_LANGUAGES
 *  - 링크 만료 "체크아웃 14일 뒤": `apps/api/api/core/config.py` guest_link_expire_days
 *  - OTA 예약 → 게스트 링크 자동 발급: `apps/api/api/services/channex/bookings.py`
 *  - 객실 타입·요금 양방향 / 재고는 내보내기만: CLAUDE.md "PMS/CMS"
 *  - 베타 게이트: `apps/api/api/routes/auth.py` BETA_GATE_MESSAGE
 *
 * 화면 캡처(`public/guides/*.webp`)는 2026-10-29 에 지금 브랜드·데모 숙소("바다숲 스테이 제주")로
 * 다시 찍었다. 테스트 데이터(QA 계정·QA 숙소 이름)가 대외 표면에 나오지 않게 할 것.
 */
import type { StayStop } from "@/lib/stay-clock";

export const HERO = {
  titleLines: ["예약부터 체크아웃까지,", "게스트 응대를 한 줄로."],
  lede: "OTA 예약이 들어오면 게스트 안내 링크가 저절로 만들어져요. 게스트는 앱 설치 없이 자기 언어로 묻고, 답은 숙소가 적어 둔 안내문에서 나와요.",
  secondaryCta: { to: "/guide", label: "사용 가이드" },
  betaNote: "현재 베타 테스터만 가입을 허용하고 있어요.",
  image: {
    src: "/landing/hero-lobby.webp",
    // 모바일은 이 사진이 첫 화면의 가장 큰 요소(LCP)라 원본(161KB) 대신 폭에 맞는 걸 받는다.
    srcSet:
      "/landing/hero-lobby-640w.webp 640w, /landing/hero-lobby-828w.webp 828w, /landing/hero-lobby-1080w.webp 1080w, /landing/hero-lobby.webp 1536w",
    // 데스크톱은 가로 사진을 세로로 잘라 쓰므로 칸 폭보다 1.5배쯤 넓은 원본이 필요하다.
    sizes: "(min-width: 768px) 560px, 100vw",
    alt: "오후 햇살이 드는 숙소 로비, 나무 프런트 데스크 위에 놓인 호출 벨",
    width: 1536,
    height: 1024,
  },
} as const;

export type StayStation = StayStop & {
  id: string;
  /** 체류 띠에 쓰는 짧은 이름. */
  short: string;
  title: string;
  body: string;
  /**
   * 캡처 안에서 보여 줄 부분. 내용이 화면 가운데에만 있는 캡처(언어 선택·만료 안내)는 키우고,
   * 계약서는 위쪽 숙소 이름(테스트 데이터)이 잘리도록 아래 기준으로 키운다.
   */
  screen: { src: string; alt: string; crop?: { scale: number; origin: string } };
};

export const STORY = {
  title: "숙박 한 번, 질문은 이 시각에 와요.",
  lede: "아래 화면은 전부 실제 게스트 화면이에요. 내려갈수록 체류 시계가 흘러가요.",
};

export const STATIONS: StayStation[] = [
  {
    id: "booked",
    day: -3,
    minute: 14 * 60 + 2,
    short: "예약 확정",
    title: "OTA 예약이 들어와요",
    body: "Booking.com 같은 OTA 예약이 채널 매니저를 거쳐 스테이라인에 들어오면, 이 게스트만 여는 안내 링크가 함께 만들어져요. 운영자는 링크를 보내기만 하면 돼요.",
    screen: {
      src: "/guides/guest-01-language.webp",
      alt: "게스트 링크를 처음 열면 보이는 언어 선택 화면. 한국어, English, 中文, 日本語 버튼",
      crop: { scale: 1.12, origin: "50% 52%" },
    },
  },
  {
    id: "opened",
    day: -1,
    minute: 21 * 60 + 40,
    short: "링크 열람",
    title: "게스트가 링크를 열어요",
    body: "로그인도 앱 설치도 없어요. 언어를 고르면 환영 인사와 질문 버튼이 그 언어로 바뀌고, 답은 게스트가 묻는 언어로 와요. 한국어, English, 中文, 日本語를 지원해요.",
    screen: {
      src: "/guides/guest-05-language-en.webp",
      alt: "영어로 바뀐 환영 화면과 체크아웃 시간, 짐 보관 질문에 대한 답변",
    },
  },
  {
    id: "checkin",
    day: 0,
    minute: 15 * 60,
    short: "체크인",
    title: "체크인 전에 계약서까지",
    body: "숙박 계약서를 등록해 두면 게스트가 링크 안에서 읽고 동의해요. 동의한 계약서는 게스트도 운영자도 다시 열어 볼 수 있어요.",
    screen: {
      src: "/guides/guest-09-contract.webp",
      alt: "게스트 이름, 객실, 체크인 날짜와 조항이 담긴 숙박 계약서, 아래에 동의 버튼",
    },
  },
  {
    id: "breakfast",
    day: 0,
    minute: 16 * 60 + 20,
    short: "조식 질문",
    title: "버튼 하나에 사진까지",
    body: "‘조식 안내’ 같은 질문 버튼을 누르면 안내문에 붙여 둔 사진까지 답에 같이 나와요. 같은 질문에 매번 같은 답을 하던 일이 줄어요.",
    screen: {
      src: "/guides/guest-04-guide-image-reply.webp",
      alt: "‘조식 안내’ 질문 버튼에 조식 시간과 메뉴, 라운지 사진 묶음(+2)으로 답한 대화",
    },
  },
  {
    id: "night",
    day: 1,
    minute: 2 * 60 + 10,
    night: true,
    short: "새벽 와이파이",
    title: "새벽 2시, 프런트가 비어 있어도",
    body: "와이파이 비밀번호처럼 급한 질문은 꼭 밤에 와요. 컨시어지는 잠들지 않고, 숙소가 등록한 안내문과 객실 정보에서 답을 찾아요.",
    screen: {
      src: "/guides/guest-08-room-info.webp",
      alt: "객실 와이파이와 도어락, 편의시설을 묻는 질문에 배정된 호실 정보로 답한 대화",
    },
  },
  {
    id: "checkout",
    day: 1,
    minute: 11 * 60,
    short: "체크아웃",
    title: "체크아웃, 그리고 링크가 닫혀요",
    body: "체크아웃 14일 뒤에는 링크가 더 열리지 않아요. 게스트와 나눈 대화는 운영자 콘솔에서 다시 볼 수 있어요.",
    screen: {
      src: "/guides/guest-06-invalid.webp",
      alt: "만료된 링크를 열면 보이는 ‘유효하지 않은 링크입니다’ 안내 화면",
      crop: { scale: 1.12, origin: "50% 49%" },
    },
  },
];

export const STATEMENT = {
  title: "모르는 건 지어내지 않아요.",
  body: "컨시어지는 숙소가 직접 적은 안내문과 숙소·객실 정보만 보고 답해요. 거기에 없는 질문이면 추측하지 않고 프런트 연락처를 알려 줘요. 안내문을 고치면 답도 그날 바로 바뀌어요.",
};

export const CONSOLE = {
  title: "운영자 쪽은 콘솔 하나예요.",
  lede: "예약, 객실, 채널, 안내문이 같은 숙소 기준으로 움직여요.",
  /** 체류의 어느 시점에 쓰는 기능인지(`when`)로 줄을 세운다 — 위 스토리의 선이 여기까지 이어진다. */
  rows: [
    {
      when: "D-3 14:02 · 예약 확정",
      name: "채널 연동",
      body: "객실 타입과 요금은 채널 매니저와 양쪽으로 맞춰지고, 재고는 OTA로 내보내요. OTA 예약은 자동으로 들어와요.",
    },
    {
      when: "D-day 15:00 · 체크인",
      name: "예약 · 프런트",
      body: "예약을 만들면 게스트 링크가 발급되고, 체크인할 때 호실을 배정해요.",
    },
    {
      when: "체류 내내",
      name: "객실현황판 · 타임라인",
      body: "호실별 일정을 한 화면에서 보고, 끌어서 옮겨요.",
    },
    {
      when: "D-day 16:20 · D+1 02:10",
      name: "안내문 · FAQ",
      body: "마크다운으로 쓰고 사진을 붙이면 그대로 컨시어지가 읽는 자료가 돼요.",
    },
    {
      when: "D+1 11:00 이후",
      name: "리포트",
      body: "기간별 점유율과 매출, 채널별 매출을 봐요.",
    },
    {
      when: "모든 숙소, 모든 체류",
      name: "여러 숙소 · 멤버",
      body: "계정 하나로 숙소를 여러 개 등록하고, 직원을 초대해 역할마다 권한을 나눠요.",
    },
  ],
};

export const SETUP = {
  title: "세 단계면 첫 링크가 나가요.",
  steps: [
    {
      title: "숙소와 객실 등록",
      body: "체크인 시간, 와이파이, 소개 문구와 객실 타입, 호실을 넣어요.",
    },
    {
      title: "안내문 쓰기",
      body: "체크인 방법, 주차, 주변 정보처럼 자주 받는 질문부터 적어요.",
    },
    {
      title: "예약에 링크 붙여 보내기",
      body: "예약이 생기면 링크가 만들어져요. 보내는 순간부터 컨시어지가 받아요.",
    },
  ],
};

export const FINALE = {
  title: "제휴 문의는 메일로 받아요.",
  body: "숙소 운영사든 서비스 파트너든, 같이 해 보고 싶은 일이 있다면 편하게 보내 주세요.",
  betaNote: "가입한 계정은 베타 승인 뒤에 열려요. 승인 요청도 같은 주소로 받아요.",
  secondaryCta: { to: "/faq", label: "자주 묻는 질문" },
};
